/**
 * @file 계약서·견적서 PDF 프록시 헬퍼
 *
 * ## 왜 이 파일이 있나
 * PDF 는 비공개 버킷(`documents`)에 있다. 예전에는 화면마다
 *   ① 서버에 「서명 주소 하나 만들어 줘」 요청 → ② 그 주소를 브라우저가 열기
 * 두 단계를 밟았는데, 그 주소는 1시간이면 죽고 저장할 수도 없다. 실제로 저장된
 * 주소를 그대로 여는 화면이 하나 있었고, 거기서 Supabase 의
 * `{"error":"Bucket not found"}` JSON 이 새 탭에 떴다(2026-08).
 *
 * 이제는 **서버가 파일을 대신 받아 흘려보내는 주소**를 부른다. 만료가 없고,
 * 권한 확인이 요청마다 일어난다. 다만 인증 헤더가 필요하므로 `<a href>` 로 직접
 * 걸 수는 없고 — 여기서 blob 으로 받아 저장한다.
 *
 * 새 화면을 만들 때 `pdfUrl` 값을 링크로 걸지 말 것. 그 값은 「PDF 가 준비됐는가」
 * 판단에만 쓰고, 실제로 여는 건 이 헬퍼로 한다.
 */

import { instance } from "./api-client";

/** 계약서 PDF 스트리밍 경로. 계약 id 로 고정 — 만료 없음. */
export const contractPdfPath = (contractId: string): string =>
  `/contracts/${contractId}/pdf`;

/** 견적서 PDF 스트리밍 경로. 견적 버전 id 로 고정 — 만료 없음. */
export const quotePdfPath = (versionId: string): string =>
  `/quote-responses/versions/${versionId}/pdf`;

/** 프록시에서 PDF 바이트를 받아온다. 인라인 미리보기를 붙일 때 쓴다. */
export async function fetchPdfBlob(path: string): Promise<Blob> {
  const response = await instance.get<Blob>(path, { responseType: "blob" });
  return response.data;
}

/**
 * 프록시에서 받아 곧바로 저장한다.
 *
 * `revokeObjectURL` 을 즉시 부르면 저장이 취소되는 브라우저가 있어 늦춘다.
 */
export async function downloadPdf(
  path: string,
  fileName: string,
): Promise<void> {
  const blob = await fetchPdfBlob(path);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
